/**
 * Shared formatting utilities for DFP plugins
 * Provides consistent formatting across all components
 */

/**
 * Format trend value with optional percentage sign
 * @param value - Numeric value to format
 * @param isPercent - Whether to add percentage sign
 * @returns Formatted string (e.g., "+5.00%" or "+5.00")
 */
export const formatTrendValue = (value: number | null | undefined, isPercent: boolean = false): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (isPercent) {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
};

/**
 * Format effort hours to human-readable string
 * @param hours - Hours to format
 * @returns Formatted string (e.g., "30m", "5h", "2d", "2d 4h")
 */
export const formatEffortHours = (hours: number | null | undefined): string => {
  if (hours === null || hours === undefined) {
    return 'N/A';
  }
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 8) return `${hours}h`;
  const days = Math.floor(hours / 8);
  const remainingHours = hours % 8;
  if (remainingHours === 0) return `${days}d`;
  return `${days}d ${remainingHours}h`;
};

/**
 * Format date for API calls (YYYY-MM-DD format)
 * @param date - Date object to format
 * @returns Formatted date string
 */
export const formatDateForAPI = (date: Date | null | undefined): string => {
  if (!date) {
    return '';
  }
  return date.toISOString().split('T')[0];
};

/**
 * Format number with specified decimal places
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  return value.toFixed(decimals);
};

/**
 * Format percentage value
 * @param value - Number to format as percentage (0-1 or 0-100)
 * @param decimals - Number of decimal places (default: 2)
 * @param isDecimal - Whether value is already in decimal form (0-1) vs percentage (0-100)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number | null | undefined, decimals: number = 2, isDecimal: boolean = false): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Get date range for last N days
 * @param days - Number of days to go back
 * @returns Object with start and end date strings
 */
export const getDateRange = (days: number): { start: string; end: string } => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  
  return {
    start: formatDateForAPI(startDate),
    end: formatDateForAPI(endDate),
  };
};

/**
 * Format a date to human-readable format as per DFP standards
 * Format: "August 30 2025, 6:12:14 AM"
 * Locale: en-US
 *
 * @param date - Date object or date string to format
 * @returns Formatted date string
 */
export const formatDateHumanReadable = (date: Date | string | null | undefined): string => {
  if (!date) {
    return 'N/A';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  return dateObj.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};
